import { useCallback, useEffect, useRef, useState } from "react";
import { useUnmount } from "react-use";
import { LiveChatJoinResponse } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { WebRTCConnection } from "@scrile/api-provider/dist/api/WebRTCProvider";
import { isEqual, uniqBy } from "lodash-es";
import {
  addTracksToStream,
  cloneScreenTracks,
  getScreenTracks,
  getVideoTracks,
  removeTracksFromStream,
} from "lib/mediaStreamHelpers";
import providers from "lib/providers";
import { MediaConstraints } from "types";
import { isDesktop } from "../desktop";
import { getScreenCaptureSources } from "../desktop/ipc";
import useBlurController from "./useBlurController";
import useMediaConstraints from "./useMediaConstraints";
import useWebRTC from "./useWebRTC";
import { SendDataEvent } from "../const";

interface ProduceProps {
  token: string;
  joinData: LiveChatJoinResponse | null;
  autoProduceStream: boolean;
  voiceOnly?: boolean;
}

// While muted, re-broadcast the mute state on this cadence so a consumer that
// attached after we muted (a late joiner, or a reconnect that recreated the
// consumer) converges to the correct mic_off indicator. Matches the ~1s
// producer-presence cadence; idempotent on the consumer side.
const MUTE_REBROADCAST_INTERVAL_MS = 2000;

function useProduceController({ token, joinData, autoProduceStream, voiceOnly }: ProduceProps) {
  const [producerSpeaking, setProducerSpeaking] = useState(false);
  // SDK auto-runs an AudioAnalyzer on mic stream every 500 ms after
  // produceStream(); we hook into its onAudioActivity callback to flip a
  // local boolean for the active-speaker indicator on our own tile.
  // The same data is also broadcast over the producer's data channel —
  // remote viewers get it via the consumer-side `audio-activity` event.
  const WebRTC = useWebRTC({
    audioAnalyzerOptions: {
      onAudioActivity: (data) => setProducerSpeaking(!!data.isSpeaking),
    },
  });
  const { blurStart, blurStop, setBackgroundImage, setBackgroundMode } = useBlurController();
  const [stream, setStream] = useState<MediaStream>(new MediaStream([]));
  const [screenStream, setScreenStream] = useState<MediaStream>(new MediaStream([]));
  const [streamToProduce, setStreamToProduce] = useState<MediaStream>(new MediaStream([]));
  // Refs mirror the stream state so reconnect-time cleanup (`onProduceStream`)
  // sees the LATEST tracks. The memoised `useCallback` for `onProduceStream`
  // does not list these states as deps (would invalidate the callback on
  // every camera/mic toggle), so the closure value would otherwise be stale.
  const streamRef = useRef(stream);
  streamRef.current = stream;
  const screenStreamRef = useRef(screenStream);
  screenStreamRef.current = screenStream;
  const streamToProduceRef = useRef(streamToProduce);
  streamToProduceRef.current = streamToProduce;
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const processingRef = useRef(false);
  // Set by onAudioChange when a mic toggle was handled as a cheap mute
  // (track.enabled flip, no track add/remove). onChangeConstraints reads it to
  // skip the WebRTC renegotiation and instead emit an AUDIO_MUTED data signal.
  const audioMuteOnlyRef = useRef(false);
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);

  const connection = useRef<WebRTCConnection>();
  const onChangeConstraintsRef = useRef<(constraints: MediaConstraints) => Promise<void>>();
  const { mediaConstraints, setMediaConstraints } = useMediaConstraints({
    video: true,
    audio: true,
    videoEnabled: autoProduceStream && !voiceOnly,
    audioEnabled: autoProduceStream,
    shareEnabled: false,
    blurEnabled: false,
  });

  const setStreams = (stream: MediaStream) => {
    const screenTracks = getScreenTracks(stream);
    screenTracks.forEach((t) => {
      t.onended = () => onChangeConstraintsRef.current?.({ shareEnabled: false });
    });
    setStreamToProduce(new MediaStream(stream));
    setStream(new MediaStream(getVideoTracks(stream)));
    setScreenStream(new MediaStream(screenTracks));
  };

  const onScreenShare = async (constraints: MediaConstraints) => {
    let data: MediaStreamConstraints | MediaStream;
    if (constraints.shareEnabled) {
      let screen: MediaStream;
      if (isDesktop) {
        // Electron: use desktopCapturer via IPC (getDisplayMedia not supported)
        const sources = await getScreenCaptureSources();
        if (!sources.length) throw new Error("No screen sources available");
        // Pick first screen source (Entire Screen)
        const screenSource = sources.find((s) => s.type === "screen") || sources[0];
        screen = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            mandatory: {
              chromeMediaSource: "desktop",
              chromeMediaSourceId: screenSource.id,
            },
          } as any,
        });
      } else {
        // @ts-ignore
        screen = (await navigator.mediaDevices.getDisplayMedia()) as MediaStream;
      }
      const screenTracks = screen.getVideoTracks();
      // @ts-ignore
      screenTracks.forEach((t) => (t.kindType = "screen"));
      // Mark screen-audio (when user picks "share tab audio") so downstream
      // consumers can distinguish it from the mic — per-speaker transcription
      // must read mic only, not whatever happens to play on the shared tab.
      const screenAudioTracks = screen.getAudioTracks();
      // @ts-ignore
      screenAudioTracks.forEach((t) => (t.kindType = "screen"));
      data = new MediaStream([...screen.getTracks(), ...streamToProduce.getVideoTracks()]);
      addTracksToStream(data, streamToProduce.getAudioTracks(), true);
    } else {
      removeTracksFromStream(streamToProduce, getScreenTracks(streamToProduce));
      data = new MediaStream(streamToProduce.getVideoTracks());
      addTracksToStream(data, streamToProduce.getAudioTracks(), true);
    }
    return data;
  };

  const onVideoChange = async (constraints: MediaConstraints) => {
    let data: MediaStream;

    removeTracksFromStream(streamToProduce, getVideoTracks(streamToProduce));
    if (!constraints.videoEnabled) {
      data = new MediaStream(streamToProduce.getVideoTracks());
      addTracksToStream(data, streamToProduce.getAudioTracks(), true);
    } else {
      let newStream: MediaStream;
      try {
        newStream = new MediaStream(await navigator.mediaDevices.getUserMedia({ video: constraints.video }));
      } catch (e) {
        if (e instanceof OverconstrainedError) {
          // eslint-disable-next-line no-console
          console.warn("Video constraints not supported, falling back to default camera", e.constraint);
          newStream = new MediaStream(await navigator.mediaDevices.getUserMedia({ video: true }));
          constraints.video = true;
        } else {
          throw e;
        }
      }
      addTracksToStream(newStream, streamToProduce.getAudioTracks(), true);
      cloneScreenTracks(getScreenTracks(streamToProduce)).forEach((t) => newStream.addTrack(t));
      data = newStream;
    }
    return data;
  };

  // Best-effort mute signal to consumers over the producer's data channel. The
  // cheap-mute path keeps the audio producer open, so producers-updated can't
  // convey mute — this explicit signal is the only source for the remote
  // mic_off indicator. Optional-chained: a no-op until the producer exists.
  const sendAudioMuted = useCallback(
    (muted: boolean) => {
      // best-effort; local mic state is already correct even if this drops
      WebRTC.webRTCData?.producer.client?.sendData({ event: SendDataEvent.AUDIO_MUTED, muted })?.catch(() => {});
    },
    [WebRTC]
  );

  const onAudioChange = async (constraints: MediaConstraints) => {
    const deviceChanged = !isEqual(mediaConstraints.audio, constraints.audio);
    // Only flip enabled on LIVE tracks — a track whose readyState went 'ended'
    // mid-mute (OS revoked the mic, device unplugged) must fall through to the
    // re-acquire path below, else unmute would silently enable a dead track.
    const liveAudioTracks = streamToProduce.getAudioTracks().filter((t) => t.readyState === "live");

    // Cheap mute path: only the audioEnabled flag toggled (mic DEVICE unchanged)
    // and we still hold a live audio track. Flip track.enabled in place instead
    // of stopping the track and renegotiating — mic on/off becomes instant (no
    // getUserMedia + produceStream round-trip, ~1-2s). The track stays
    // readyState='live', so per-speaker recording keeps a time-aligned silent
    // track during mute (see useConferenceRecording). The audio producer stays
    // open, so consumers are told via the AUDIO_MUTED signal in onChangeConstraints.
    if (!deviceChanged && liveAudioTracks.length > 0) {
      liveAudioTracks.forEach((t) => (t.enabled = !!constraints.audioEnabled));
      audioMuteOnlyRef.current = true;
      return streamToProduce;
    }
    audioMuteOnlyRef.current = false;

    if (deviceChanged || !constraints.audioEnabled) {
      removeTracksFromStream(streamToProduce, streamToProduce.getAudioTracks());
    }
    if (constraints.audioEnabled) {
      let newStream: MediaStream;
      try {
        newStream = await navigator.mediaDevices.getUserMedia({
          audio: constraints.audio,
        });
      } catch (e) {
        if (e instanceof OverconstrainedError) {
          // eslint-disable-next-line no-console
          console.warn("Audio constraints not supported, falling back to default mic", e.constraint);
          newStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          constraints.audio = true;
        } else {
          throw e;
        }
      }
      addTracksToStream(streamToProduce, newStream.getAudioTracks(), true);
    }
    return streamToProduce;
  };

  const onBlurChange = useCallback(
    async (constraints: MediaConstraints, outStream = streamToProduce) => {
      let data: MediaStream;

      if (constraints.blurEnabled) {
        const videoTracks = getVideoTracks(outStream);
        if (!videoTracks.length) {
          constraints.blurEnabled = false;
          return outStream;
        }
        try {
          data = await blurStart(videoTracks[0]);
        } catch (e) {
          // Segmenter init can reject — most likely the one-time model fetch from
          // tfhub.dev failed (offline / strict CSP / service deprecation). Degrade
          // to the unblurred stream and leave blur off rather than rejecting, which
          // would surface as an unhandled rejection and wedge the toggle.
          // eslint-disable-next-line no-console
          console.warn("[call] Failed to enable background blur:", e);
          constraints.blurEnabled = false;
          return outStream;
        }
      } else {
        data = await blurStop();
      }

      addTracksToStream(data, outStream.getAudioTracks(), true);
      cloneScreenTracks(getScreenTracks(streamToProduce)).forEach((t) => data.addTrack(t));
      return data;
    },
    [blurStart, blurStop, streamToProduce]
  );

  const setDevicesList = useCallback(async () => {
    const { audioInputList, videoInputList } = await WebRTC.getDeviceList();
    setAudioInputs(uniqBy(audioInputList.reverse(), "groupId").reverse());
    setVideoInputs(videoInputList);
  }, [WebRTC]);

  const closeStream = async () => {
    try {
      await onProducerFinishedStream();
    } catch {
      /* ignore */
    }
    WebRTC.closeAllStreams();
    const stopTracks = (s: MediaStream | null) => {
      s?.getTracks().forEach((t) => {
        try {
          t.stop();
        } catch {
          /* already stopped */
        }
      });
    };
    stopTracks(streamToProduce);
    stopTracks(stream);
    stopTracks(screenStream);
  };

  const onProduceStream = useCallback(async () => {
    try {
      setLoading(true);
      if (!token) return false;
      // RECONNECT SAFETY: explicitly stop the previous getUserMedia tracks
      // before requesting fresh ones. Without this, Safari's hardware lock
      // does not release between rebuilds and `navigator.mediaDevices.
      // getUserMedia` either hangs with "device already in use" or hands
      // back the SAME track — both ways break the new producer. Chrome
      // is more forgiving but leaks track objects across reconnect cycles
      // (audible as duplicated mic streams after a few flaps). The
      // pattern mirrors the 2026-05-30 fix in `useCallController.
      // reconnectStreams` for 1-on-1; here is the group-call equivalent.
      //
      // Reads through refs (NOT closure values) — this callback is
      // memoised with deps that exclude the stream state, so the closure
      // would otherwise pin the initial empty MediaStreams forever and
      // do nothing on reconnect.
      const stopAndClear = (s: MediaStream | null | undefined) => {
        try { s?.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } }); } catch { /* ignore */ }
      };
      stopAndClear(streamRef.current);
      stopAndClear(streamToProduceRef.current);
      stopAndClear(screenStreamRef.current);

      const [connectionData] = await Promise.all([providers.LivechatsProvider.broadcast({ token }), setDevicesList()]);
      connection.current = connectionData;

      let localStream = new MediaStream([]);
      if (mediaConstraints.videoEnabled || mediaConstraints.audioEnabled) {
        try {
          localStream = await navigator.mediaDevices.getUserMedia({
            video: mediaConstraints.videoEnabled ? mediaConstraints.video : false,
            audio: mediaConstraints.audioEnabled ? mediaConstraints.audio : false,
          });
        } catch (e) {
          if (e instanceof OverconstrainedError) {
            // eslint-disable-next-line no-console
            console.warn("Saved constraints not supported, falling back to defaults", e.constraint);
            localStream = await navigator.mediaDevices.getUserMedia({
              video: mediaConstraints.videoEnabled,
              audio: mediaConstraints.audioEnabled,
            });
            setMediaConstraints((prev) => ({
              ...prev,
              video: prev.videoEnabled ? true : prev.video,
              audio: prev.audioEnabled ? true : prev.audio,
            }));
          } else {
            // Permission denied or no camera — proceed without media
            // eslint-disable-next-line no-console
            console.warn("[produce] getUserMedia failed, continuing without local media:", e);
            setMediaConstraints((prev) => ({
              ...prev,
              videoEnabled: false,
              audioEnabled: false,
            }));
          }
        }

        // Apply blur only if video is enabled and blur was explicitly requested
        // Skip on initial auto-produce to avoid blocking UI with heavy WASM load
        if (localStream.getTracks().length > 0 && mediaConstraints.blurEnabled && mediaConstraints.videoEnabled) {
          localStream = await onBlurChange({ ...mediaConstraints }, localStream);
        }
      }

      localStream = await WebRTC.produceStream(connection.current, localStream);
      setStreams(localStream);
      return true;
    } finally {
      setLoading(false);
    }
  }, [WebRTC, setDevicesList, token, mediaConstraints, onBlurChange, setMediaConstraints]);

  const onChangeConstraints = async (constraints: MediaConstraints) => {
    if (processingRef.current) return;
    try {
      processingRef.current = true;
      setProcessing(true);
      audioMuteOnlyRef.current = false;
      let data: MediaStream | null = null;
      let audioChanged = false;
      const localConstraints: MediaConstraints = {
        ...mediaConstraints,
        ...constraints,
      };
      if (!isEqual(mediaConstraints.blurEnabled, localConstraints.blurEnabled)) {
        data = await onBlurChange(localConstraints);
      } else if (!isEqual(mediaConstraints.shareEnabled, localConstraints.shareEnabled)) {
        data = await onScreenShare(localConstraints);
      } else if (
        !isEqual(mediaConstraints.videoEnabled, localConstraints.videoEnabled) ||
        !isEqual(mediaConstraints.video, localConstraints.video)
      ) {
        if (localConstraints.blurEnabled) {
          await blurStop();
        }
        data = await onVideoChange(localConstraints);
        if (localConstraints.blurEnabled && localConstraints.videoEnabled) {
          data = await onBlurChange(localConstraints, data);
        }
      } else if (
        !isEqual(mediaConstraints.audio, localConstraints.audio) ||
        !isEqual(mediaConstraints.audioEnabled, localConstraints.audioEnabled)
      ) {
        audioChanged = true;
        data = await onAudioChange(localConstraints);
      }
      // Cheap mute/unmute: the audio track's enabled flag was flipped in place,
      // so no renegotiation is needed. Skip produceStream/setStreams, just sync
      // local state and tell consumers (their producers-updated still reports
      // audio:true because the producer is still open).
      if (audioMuteOnlyRef.current) {
        sendAudioMuted(!localConstraints.audioEnabled);
        setMediaConstraints(localConstraints);
        return;
      }
      if (!connection.current || !data) {
        setMediaConstraints(localConstraints);
        return;
      }
      const newStream = await WebRTC.produceStream(connection.current, data);
      setStreams(newStream);
      // A non-cheap audio change (mic device switch) renegotiated the producer.
      // Re-acquired tracks are unmuted, so tell consumers to clear any stale
      // mute state (only when the audio branch ran — never on video/screen).
      if (audioChanged) {
        sendAudioMuted(!localConstraints.audioEnabled);
      }
      setMediaConstraints(localConstraints);
    } finally {
      processingRef.current = false;
      setProcessing(false);
    }
  };

  onChangeConstraintsRef.current = onChangeConstraints;

  const shouldCallProduceStream = useRef(true);
  useEffect(() => {
    if (joinData && shouldCallProduceStream.current && autoProduceStream) {
      shouldCallProduceStream.current = false;
      onProduceStream().catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("[produce] Auto-produce failed:", e);
      });
    }
  }, [joinData, onProduceStream, autoProduceStream]);

  // Late-joiner mute sync: AUDIO_MUTED is a one-shot data-channel broadcast, so
  // a consumer that attaches AFTER we muted (new participant, or a reconnect
  // that recreated the consumer) would otherwise show our mic as ON. While
  // muted, periodically re-broadcast — onProducerData no-ops when unchanged, so
  // this is cheap for existing viewers and converges late ones within one tick.
  useEffect(() => {
    if (mediaConstraints.audioEnabled) return;
    const id = setInterval(() => sendAudioMuted(true), MUTE_REBROADCAST_INTERVAL_MS);
    return () => clearInterval(id);
  }, [mediaConstraints.audioEnabled, sendAudioMuted]);

  useUnmount(closeStream);

  const onProducerFinishedStream = async () => {
    await WebRTC.webRTCData?.producer.client?.sendData({ event: SendDataEvent.PRODUCER_FINISHED_STREAM });
  };

  return {
    processing,
    loading,
    screenStream,
    stream,
    streamToProduce,
    connection: connection.current,
    mediaConstraints,
    audioInputs,
    videoInputs,
    onProduceStream,
    onProducerFinishedStream,
    onChangeConstraints,
    closeStream,
    setBackgroundImage,
    setBackgroundMode,
    producerSpeaking,
  };
}

export default useProduceController;
